import { buildCSV, exportAsFile } from '@mairistem/tools';
import { NIVEAUX } from '../../constants/pes/importPes';
import type {
	AnomalieImportPes,
	CompteRenduImportPes,
	NiveauAnomalieImportPes,
} from '../../services/pes/importPes';
import { normaliser } from './format';

/** Clé stable d'un groupe d'anomalies (niveau, cause, message) dans le compte rendu. */
export const cleAnomalie = (anomalie: AnomalieImportPes, rang: number) =>
	`${anomalie.niveau}|${anomalie.code}|${rang}`;

/** Nombre de causes de chaque niveau. */
export const compterParNiveau = (anomalies: AnomalieImportPes[]) =>
	anomalies.reduce<Record<NiveauAnomalieImportPes, number>>(
		(nombres, anomalie) => {
			nombres[anomalie.niveau]++;
			return nombres;
		},
		{ bloquant: 0, avertissement: 0, information: 0 },
	);

/** Texte où chercher une saisie de l'utilisateur (normalisé) : message, détails et pièces citées. */
export const texteAnomalie = (anomalie: AnomalieImportPes) =>
	normaliser(
		[
			anomalie.message,
			anomalie.code,
			...anomalie.details,
			...anomalie.pieces.flatMap((piece) => [
				piece.fichier,
				piece.bordereau,
				piece.piece,
				piece.objet ?? '',
			]),
		].join(' '),
	);

/**
 * Numéros de lignes en plages lisibles : « 1 à 364 », « 1 à 4, 7, 9 à 12 » ; une pièce de paye cite des centaines de
 * lignes.
 */
export const plagesLignes = (lignes: string[]) => {
	const numeros = [...new Set(lignes.map(Number))]
		.filter(Number.isInteger)
		.sort((a, b) => a - b);
	const autres = lignes.filter((ligne) => !Number.isInteger(Number(ligne)));
	const plages: string[] = [];
	for (let debut = 0; debut < numeros.length; ) {
		let fin = debut;
		while (fin + 1 < numeros.length && numeros[fin + 1] === numeros[fin] + 1) {
			fin++;
		}
		plages.push(
			fin > debut
				? `${numeros[debut]} à ${numeros[fin]}`
				: String(numeros[debut]),
		);
		debut = fin + 1;
	}

	return [...plages, ...autres].join(', ');
};

/** marque d'ordre des octets : Excel lit alors le CSV en UTF-8 (accents) */
const BOM = String.fromCharCode(0xfeff);

const COLONNES_CSV = [
	'Niveau',
	'Code',
	'Message',
	'Détail',
	'Fichier',
	'Bordereau',
	'Pièce',
	'Objet',
	'Lignes',
];

/**
 * Compte rendu à plat pour un tableur (buildCSV, séparateur point-virgule) : une ligne par pièce citée, ou par
 * détail pour une cause sans pièce, afin que le client corrige son fichier ligne à ligne.
 */
export const versCsv = (compteRendu: CompteRenduImportPes) =>
	buildCSV({
		columns: COLONNES_CSV,
		rows: compteRendu.anomalies.flatMap((anomalie) => {
			// toutes les colonnes renseignées : buildCSV n'accepte pas une valeur absente
			const cause = {
				Niveau: NIVEAUX[anomalie.niveau].singulier,
				Code: anomalie.code,
				Message: anomalie.message,
				Détail: anomalie.details.join(' | '),
				Fichier: '',
				Bordereau: '',
				Pièce: '',
				Objet: '',
				Lignes: '',
			};
			return anomalie.pieces.length === 0
				? [cause]
				: anomalie.pieces.map((piece) => ({
						...cause,
						Fichier: piece.fichier,
						Bordereau: piece.bordereau,
						Pièce: piece.piece,
						Objet: piece.objet ?? '',
						Lignes: plagesLignes(piece.lignes),
					}));
		}),
		delimiterCharacter: ';',
		includeHeaders: true,
	});

/** Nom du CSV tiré du fichier importé, sans extension ni caractère interdit (« : », « / » d'un fichier en attente). */
export const nomCsv = (nomFichier: string) =>
	`compte-rendu-${normaliser(nomFichier.replace(/\.(xml|zip)$/i, ''))
		.replace(/[^a-z0-9_-]+/g, '-')
		.replace(/^-|-$/g, '')}`;

export const telechargerCsv = (
	compteRendu: CompteRenduImportPes,
	nomFichier: string,
) =>
	exportAsFile(
		new Blob([BOM, versCsv(compteRendu)], { type: 'text/csv;charset=utf-8' }),
		'csv',
		nomCsv(nomFichier),
	);
